import { useEffect, useRef } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { PlanningEntityIdentifier, PlanningEntityType } from 'generated';

interface UseDropTargetProps {
  scheduleStart: string;
  start: string;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  /**
   * Registers no drop target while false, so nothing can be dropped here.
   * Everything that makes a slot undroppable — the column is loading, the slot
   * is a non-droppable travel time — belongs in this one flag.
   */
  canDrop?: boolean;
}

/**
 * Registers a slot in a route column as a Pragmatic drop target.
 *
 * Marks the hovered slot with a `data-drag-over` attribute rather than React
 * state on purpose: a drag crossing a column would otherwise re-render every
 * slot it passes over, mid-drag. Style the `[data-drag-over]` selector instead.
 */
export const useDropTarget = ({
  scheduleStart,
  start,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  canDrop = true
}: UseDropTargetProps) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !canDrop) return;

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        scheduleStart,
        start,
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier
      }),
      onDragEnter: () => el.setAttribute('data-drag-over', ''),
      onDragLeave: () => el.removeAttribute('data-drag-over'),
      onDrop: () => el.removeAttribute('data-drag-over')
    });
    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    scheduleStart,
    start,
    planningEntityId,
    planningEntityType,
    planningEntityIdentifier,
    canDrop
  ]);

  return ref;
};
